import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { AppNotification } from "../../types/notification.definition";

interface NotificationState {
  notifications: AppNotification[];
  loading: boolean;
  error: string | null;
  unreadCount: number;
}

const initialState: NotificationState = {
  notifications: [],
  loading: false,
  error: null,
  unreadCount: 0,
};

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    fetchNotificationsRequest: state => {
      state.loading = true;
      state.error = null;
    },
    fetchNotificationsSuccess: (
      state,
      action: PayloadAction<AppNotification[]>
    ) => {
      state.loading = false;
      state.notifications = action.payload;
      state.unreadCount = action.payload.filter(n => !n.isRead).length;
    },
    fetchNotificationsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    markAsReadRequest: (state, _action: PayloadAction<number>) => {
      state.error = null;
    },
    markAsReadSuccess: (state, action: PayloadAction<AppNotification>) => {
      const index = state.notifications.findIndex(
        n => n.id === action.payload.id
      );
      if (index !== -1) {
        state.notifications[index] = action.payload;
      }
      state.unreadCount = state.notifications.filter(n => !n.isRead).length;
    },
    markAsReadFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },

    markAllAsReadRequest: state => {
      state.error = null;
    },
    markAllAsReadSuccess: state => {
      state.notifications = state.notifications.map(n => ({
        ...n,
        isRead: true,
      }));
      state.unreadCount = 0;
    },
    markAllAsReadFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },

    deleteNotificationRequest: (state, _action: PayloadAction<number>) => {
      state.error = null;
    },
    deleteNotificationSuccess: (state, action: PayloadAction<number>) => {
      const removed = state.notifications.find(n => n.id === action.payload);
      state.notifications = state.notifications.filter(
        n => n.id !== action.payload
      );
      if (removed && !removed.isRead) {
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    deleteNotificationFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
  },
});

export const {
  fetchNotificationsRequest,
  fetchNotificationsSuccess,
  fetchNotificationsFailure,
  markAsReadRequest,
  markAsReadSuccess,
  markAsReadFailure,
  markAllAsReadRequest,
  markAllAsReadSuccess,
  markAllAsReadFailure,
  deleteNotificationRequest,
  deleteNotificationSuccess,
  deleteNotificationFailure,
} = notificationSlice.actions;

export default notificationSlice.reducer;
